import React, {
    FC, useContext, SVGAttributes,
    useEffect,
    useCallback,
} from 'react';
import classNames from 'classnames';
import queryString from 'query-string';

import { LocaleContext } from 'app/containers/LocaleProvider';

import {
    // collections
    mostInterestingCategoryTitleForABTest,
    // categories
    CATEGORY_MOST_INTRESTING,
    CATEGORY_MAIN,
    CATEGORY_SOUPS,
    CATEGORY_SALADS,
    CATEGORY_BREAKFASTS,
    CATEGORY_DESSERT,
    CATEGORY_UPSELL,
    CATEGORY_SNACKS,
    CATEGORY_FARM_PRODUCTS,
    CATEGORY_TITLES_MOBILE,
    CATEGORY_READY_TO_EAT,
} from 'app/const/categories';

import Best from 'app/assets/categories/best.component.svg';
import { useRouterState } from 'app/hooks/useRouterState';
import css from './MobileCategoryTab.module.scss';

interface ICategory {
    id: string,
}
interface IProps {
    category: ICategory
    isActive: boolean,
    onClick: (id: string) => void,
    newLayout: boolean,
}

type TypeTitlesCollection = {
    ru: Record<string, string>,
    en: Record<string, string>
}


const CategoriesIconsRecord: Record<string, FC<SVGAttributes<SVGElement>> | string> = {
    [CATEGORY_MOST_INTRESTING]: Best,
    [CATEGORY_MAIN]: 'main',
    [CATEGORY_SOUPS]: 'soups',
    [CATEGORY_SALADS]: 'salads',
    [CATEGORY_BREAKFASTS]: 'breakfasts',
    [CATEGORY_DESSERT]: 'desserts',
    [CATEGORY_UPSELL]: 'upsell',
    [CATEGORY_SNACKS]: 'snaks',
    [CATEGORY_FARM_PRODUCTS]: 'farm',
    [CATEGORY_READY_TO_EAT]: 'ready_to_eat',
};

const MobileCategoryTab: FC<IProps> = ({
    category, isActive, onClick, newLayout,
}: IProps) => {
    const { locale } = useContext(LocaleContext);
    const [routerState] = useRouterState();

    const { history } = routerState;

    const replaceCategoryUrlState = useCallback(
        () => {
            const currentParams = queryString.parse(history.location.search);
            delete currentParams.mc;
            const newSearch = queryString.stringify(currentParams);

            const detailPageRegex = /dish\/\d+\//;
            const isDetailPageOpened = detailPageRegex.test(window.location.pathname);
            const isMenu = window.location.pathname.includes('menu');
            if (history && !isDetailPageOpened && isMenu) {
                history?.push(`/menu/?${newSearch}`);
            }
        },
        [history],
    );

    const Icon = CategoriesIconsRecord[category.id];
    const textClasses = classNames({
        [css.mobileCategoryTitleActive]: isActive,
        [css.mobileCategoryTitle]: true,
        'body-s-medium': newLayout,
        [css.newLayout]: newLayout,
    });
    const currentCategoryTitlesCollection: TypeTitlesCollection = category.id === 'intresting'
        ? mostInterestingCategoryTitleForABTest
        : CATEGORY_TITLES_MOBILE;

    const isBestCategory = category.id === CATEGORY_MOST_INTRESTING;

    const jpgIconClasses = classNames({
        [css.mobileCategoryIcon]: true,
        [css[Icon as string]]: !isBestCategory,
    });

    const categoryTitle = currentCategoryTitlesCollection[locale as keyof TypeTitlesCollection][category.id];

    const rootClasses = classNames({
        [css.mobileCategoryTabRoot]: true,
        [css.newLayout]: newLayout,
    });

    useEffect(
        () => {
            const currentSearch = history.location.search || window.location.search;
            const params = queryString.parse(currentSearch);
            const menuCategory = params?.mc as string;
            if (menuCategory === category.id) {
                replaceCategoryUrlState();
                onClick(category.id);
            }
        },
        [history, category, onClick, replaceCategoryUrlState],
    );

    return (
        <div
            data-category-id={category.id}
            className={rootClasses}
            onClick={() => onClick(category.id)}
            role="none"
        >
            {isBestCategory ? <Icon className={css.mobileCategoryIcon} /> : <div className={jpgIconClasses} />}
            <div className={textClasses}>
                {categoryTitle}
            </div>
        </div>
    );
};

export default React.memo(MobileCategoryTab);
